import { io } from "socket.io-client";
import Constants from "./Constants";

/**
 * Live transport for the admin dashboard: one shared socket, authenticated
 * with the same JWT already stored by Login.jsx, joined server-side into
 * that manager's merchant/branch rooms (see RealtimeGateway on the backend).
 *
 * A singleton because every screen (each extending CoreEngine) may try to
 * connect on mount — connect()/on()/off() are safe to call repeatedly.
 *
 * Two rules make that safety real, and both matter:
 *
 *  - A socket is only rebuilt when the signed-in identity changes. A socket
 *    that is merely still handshaking is *not* stale: screens call connect()
 *    once from the CoreEngine constructor and again from every on(), all
 *    within the same tick, so treating "not yet connected" as stale tore down
 *    each socket a few microseconds after opening it and left every handler
 *    registered so far attached to a closed one.
 *
 *  - Handlers live here, not only on the socket. Subscriptions therefore
 *    survive a rebuild (re-login, or a reconnect that replaces the socket),
 *    which a listener attached straight to a socket instance cannot do.
 */
class SocketEngine {
  constructor() {
    this.socket = null;
    // The token the current socket was authenticated with — a different one
    // means a different account, which is the only reason to rebuild.
    this.token = null;
    // event -> Set of subscriber callbacks
    this.handlers = new Map();
    // Events that have a dispatcher attached to the *current* socket. The
    // dispatcher reads `handlers` when it fires, so adding a subscriber to an
    // already-dispatched event needs no further work on the socket.
    this.bound = new Set();
  }

  connect() {
    const token = sessionStorage.getItem("token");
    if (!token) return null;

    if (this.socket && this.token === token) return this.socket;

    // Different account than the live socket was opened for (or first call).
    if (this.socket) this.socket.close();

    this.token = token;
    this.bound = new Set();
    this.socket = io(Constants.serverlink, {
      transports: ["websocket", "polling"],
      autoConnect: true,
      auth: { token, role: "manager" },
    });

    this.socket.on("connect_error", (err) => {
      console.warn("[socket] connect error", err.message);
    });

    for (const event of this.handlers.keys()) this.bindEvent(event);

    return this.socket;
  }

  /** Attaches the single fan-out listener for `event` to the live socket. */
  bindEvent(event) {
    if (!this.socket || this.bound.has(event)) return;
    this.bound.add(event);
    this.socket.on(event, (payload) => {
      // Copied: a subscriber may unsubscribe (screen unmount) while we run.
      for (const handler of Array.from(this.handlers.get(event) || [])) {
        handler(payload);
      }
    });
  }

  disconnect() {
    if (this.socket) this.socket.close();
    this.socket = null;
    this.token = null;
    this.bound = new Set();
    this.handlers = new Map();
  }

  on(event, handler) {
    if (!this.handlers.has(event)) this.handlers.set(event, new Set());
    this.handlers.get(event).add(handler);
    this.connect();
    this.bindEvent(event);
  }

  off(event, handler) {
    const set = this.handlers.get(event);
    if (set) set.delete(handler);
    // The dispatcher stays attached — it simply fans out to nobody, and is
    // needed again the moment another screen subscribes.
  }
}

export default new SocketEngine();
